import React from 'react';
import { RowSection, SubSection, Rows, hasRows, money, num, pretty, yesno, pair } from '../StatRows';
import SplitBar from '../charts/SplitBar';

/* "Sports" accordion: the athlete's own sport, the department's money and
   staffing, and every sport the school fields.
   Mirrors `yourSport` + `programCoaching` + `allSports` in MatchDetailSheet.swift.

   "Sport's division" has no iOS/Android equivalent — it is real data the
   payload carries and web has always shown it, so it stays. */

/** Single source of truth for both the render and the `hasContent` predicate,
    so the accordion header can never appear over an empty panel. */
function build(detail) {
  const sp = detail.athlete_sport || {};
  const a = detail.athletics || {};
  return {
    a,
    sports: Array.isArray(detail.sports_offered) ? detail.sports_offered : [],
    yourSport: [
      { label: 'Offered here', value: yesno(sp.offered) },
      { label: "Sport's division", value: pretty(sp.division) },
      { label: 'Roster size', value: num(sp.roster_size) },
      { label: 'Program spend / athlete', value: money(sp.spend_per_athlete) },
      { label: 'Head coach', value: sp.head_coach },
    ],
    program: [
      { label: 'Athletes — men / women', value: pair(num(a.athletes_men), num(a.athletes_women)) },
      { label: 'Varsity teams', value: num(a.team_count) },
      { label: 'Head coaches', value: num(a.num_head_coaches) },
      { label: 'Assistant coaches', value: num(a.num_asst_coaches) },
      {
        label: 'Avg head-coach salary (men / women)',
        value: pair(money(a.head_coach_salary_men), money(a.head_coach_salary_women)),
      },
      { label: 'Athletics revenue', value: money(a.total_revenue) },
      { label: 'Athletics expenses', value: money(a.total_expense) },
    ],
    hasSpendSplit: a.expense_men != null || a.expense_women != null,
  };
}

export function hasContent(detail) {
  const b = build(detail);
  return hasRows(b.yourSport) || hasRows(b.program) || b.hasSpendSplit || b.sports.length > 0;
}

export default function SportsSection({ detail }) {
  const { a, sports, yourSport, program, hasSpendSplit } = build(detail);

  return (
    <>
      <RowSection title="Your Sport" rows={yourSport} />

      <SubSection
        title="Athletics — Program & Coaching"
        hasContent={hasRows(program) || hasSpendSplit}
      >
        <Rows rows={program} />
        <SplitBar
          caption="Program spend — men / women"
          leftLabel="Men" rightLabel="Women"
          left={a.expense_men} right={a.expense_women}
          leftTone="blue" rightTone="gold"
        />
      </SubSection>

      <SubSection title={`All Sports (${sports.length})`} hasContent={sports.length > 0}>
        <div className="sd-pills">
          {sports.map((s, i) => (
            <span className="sd-pill" key={`${s.sport}-${s.gender}-${i}`}>
              {pretty(s.sport)}
              {s.gender ? ` (${s.gender})` : ''}
              {s.division ? ` · ${s.division}` : ''}
            </span>
          ))}
        </div>
      </SubSection>
    </>
  );
}
